import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Award, 
  BarChart3,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  Building2,
  Trophy,
  Target,
  Calendar,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value) return 'RM 0';
  return `RM ${Number(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format number
const formatNumber = (value) => {
  if (!value) return '0';
  return Number(value).toLocaleString('en-MY');
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue }) => (
  <Card className="relative overflow-hidden">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
      <div className="p-2 bg-primary/10 rounded-lg">
        <Icon className="h-4 w-4 text-primary" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      {trend && (
        <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {trend === 'up' ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
          {trendValue}
        </div>
      )}
    </CardContent>
  </Card>
);

// Bar Chart Component (Simple CSS-based)
const SimpleBarChart = ({ data, labelKey, valueKey, maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0));
  
  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{item[labelKey]}</span>
            <span className="font-medium">{formatCurrency(item[valueKey])}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, ((item[valueKey] || 0) / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Leaderboard Component
const Leaderboard = ({ performers, loading }) => {
  const getRankBadge = (rank) => {
    const colors = {
      'EVP': 'bg-purple-500',
      'SVP': 'bg-blue-500',
      'AVP': 'bg-green-500',
      'RM': 'bg-orange-500',
      'NEG': 'bg-slate-500'
    };
    return colors[rank] || 'bg-slate-400';
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 bg-slate-100 rounded-lg" />
      ))}
    </div>;
  }

  return (
    <div className="space-y-3">
      {performers.slice(0, 10).map((performer, index) => (
        <div 
          key={performer.id || index}
          className={`flex items-center justify-between p-3 rounded-lg border ${
            index < 3 ? 'bg-gradient-to-r from-amber-50 to-transparent border-amber-200' : 'bg-white border-slate-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
              index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
            }`}>
              {index + 1}
            </div>
            <div>
              <div className="font-medium text-slate-900">{performer.nickname || performer.code}</div>
              <div className="text-xs text-slate-500">{performer.code}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {performer.rank_code && (
              <Badge className={`${getRankBadge(performer.rank_code)} text-white`}>
                {performer.rank_code}
              </Badge>
            )}
            <div className="text-right">
              <div className="font-semibold text-slate-900">{formatCurrency(performer.total_commission)}</div>
              <div className="text-xs text-slate-500">Commission</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Agent Hierarchy Tree
const HierarchyTree = ({ hierarchy, loading }) => {
  const rankOrder = ['EVP', 'SVP', 'AVP', 'RM', 'NEG'];
  const rankColors = {
    'EVP': 'border-purple-500 bg-purple-50',
    'SVP': 'border-blue-500 bg-blue-50',
    'AVP': 'border-green-500 bg-green-50',
    'RM': 'border-orange-500 bg-orange-50',
    'NEG': 'border-slate-500 bg-slate-50'
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-20 bg-slate-100 rounded-lg" />
      ))}
    </div>;
  }

  return (
    <div className="space-y-6">
      {rankOrder.map((rank) => {
        const agents = hierarchy[rank] || [];
        if (agents.length === 0) return null;
        
        return (
          <div key={rank}>
            <div className="flex items-center gap-2 mb-3">
              <Badge className={`${rankColors[rank]?.split(' ')[1] || 'bg-slate-100'} text-slate-800 border`}>
                {rank}
              </Badge>
              <span className="text-sm text-slate-500">({agents.length} agents)</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {agents.slice(0, 8).map((agent, idx) => (
                <div 
                  key={agent.id || idx}
                  className={`p-2 rounded-lg border-l-4 ${rankColors[rank] || 'border-slate-300 bg-slate-50'}`}
                >
                  <div className="font-medium text-sm truncate">{agent.nickname || 'N/A'}</div>
                  <div className="text-xs text-slate-500 truncate">{agent.code}</div>
                  <div className="text-xs font-semibold text-primary mt-1">
                    {formatCurrency(agent.total_commission)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Commission Workflow Status
const WorkflowStatus = ({ data, loading }) => {
  if (loading) {
    return <div className="animate-pulse h-64 bg-slate-100 rounded-lg" />;
  }

  const getStatusColor = (status) => {
    if (status === 'approved') return 'bg-green-500';
    if (status === 'pending') return 'bg-yellow-500';
    if (status === 'rejected') return 'bg-red-500';
    return 'bg-slate-400';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 px-3 font-medium text-slate-600">PIC</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Admin</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Account</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Boss</th>
            <th className="text-right py-2 px-3 font-medium text-slate-600">Count</th>
            <th className="text-right py-2 px-3 font-medium text-slate-600">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 10).map((row, idx) => (
            <tr key={idx} className="border-b hover:bg-slate-50">
              <td className="py-2 px-3">
                <Badge variant="outline" className={`${getStatusColor(row.pic_status)} bg-opacity-20`}>
                  {row.pic_status || '-'}
                </Badge>
              </td>
              <td className="py-2 px-3">
                <Badge variant="outline" className={`${getStatusColor(row.admin_status)} bg-opacity-20`}>
                  {row.admin_status || '-'}
                </Badge>
              </td>
              <td className="py-2 px-3">
                <Badge variant="outline" className={`${getStatusColor(row.account_status)} bg-opacity-20`}>
                  {row.account_status || '-'}
                </Badge>
              </td>
              <td className="py-2 px-3">
                <Badge variant="outline" className={`${getStatusColor(row.boss_status)} bg-opacity-20`}>
                  {row.boss_status || '-'}
                </Badge>
              </td>
              <td className="py-2 px-3 text-right font-medium">{formatNumber(row.count)}</td>
              <td className="py-2 px-3 text-right font-medium">{formatCurrency(row.total_commission)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Main Analytics Dashboard Component
export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [salesByYear, setSalesByYear] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [hierarchy, setHierarchy] = useState({});
  const [workflow, setWorkflow] = useState([]);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [monthlySales, setMonthlySales] = useState([]);
  
  // Date range filter state
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  
  // Period comparison state
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [period1Start, setPeriod1Start] = useState('');
  const [period1End, setPeriod1End] = useState('');
  const [period2Start, setPeriod2Start] = useState('');
  const [period2End, setPeriod2End] = useState('');

  // Quick filter presets
  const quickFilters = [
    { label: 'Q1 2025', start: '2025-01-01', end: '2025-03-31' },
    { label: 'Q2 2025', start: '2025-04-01', end: '2025-06-30' },
    { label: 'Q3 2025', start: '2025-07-01', end: '2025-09-30' },
    { label: 'Q4 2025', start: '2025-10-01', end: '2025-12-31' },
    { label: 'Q1 2024', start: '2024-01-01', end: '2024-03-31' },
    { label: 'H1 2025', start: '2025-01-01', end: '2025-06-30' },
    { label: 'H2 2024', start: '2024-07-01', end: '2024-12-31' },
    { label: 'Full 2024', start: '2024-01-01', end: '2024-12-31' },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateParams = startDate && endDate 
        ? `&start_date=${startDate}&end_date=${endDate}` 
        : '';
      
      const [summaryRes, salesRes, performersRes, hierarchyRes, workflowRes, monthlyRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/dashboard-summary?${dateParams.slice(1)}`),
        fetch(`${API_URL}/api/analytics/sales-by-year?${dateParams.slice(1)}`),
        fetch(`${API_URL}/api/analytics/top-performers?limit=20${dateParams}`),
        fetch(`${API_URL}/api/analytics/agent-hierarchy`),
        fetch(`${API_URL}/api/analytics/commission-workflow`),
        fetch(`${API_URL}/api/analytics/monthly-sales?year=${selectedYear}${dateParams}`)
      ]);

      const [summaryData, salesData, performersData, hierarchyData, workflowData, monthlyData] = await Promise.all([
        summaryRes.json(),
        salesRes.json(),
        performersRes.json(),
        hierarchyRes.json(),
        workflowRes.json(),
        monthlyRes.json()
      ]);

      setSummary(summaryData);
      setSalesByYear(salesData.data || []);
      setTopPerformers(performersData.data || []);
      setHierarchy(hierarchyData.hierarchy || {});
      setWorkflow(workflowData.data || []);
      setMonthlySales(monthlyData.data || []);
      setIsFiltered(!!summaryData.filtered);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    if (!period1Start || !period1End || !period2Start || !period2End) {
      toast.error('Please fill in all date fields for comparison');
      return;
    }
    
    try {
      const res = await fetch(
        `${API_URL}/api/analytics/compare-periods?period1_start=${period1Start}&period1_end=${period1End}&period2_start=${period2Start}&period2_end=${period2End}`
      );
      const data = await res.json();
      setComparisonData(data);
      setShowComparison(true);
      toast.success('Period comparison loaded');
    } catch (error) {
      console.error('Failed to compare periods:', error);
      toast.error('Failed to load comparison data');
    }
  };

  const applyQuickFilter = (filter) => {
    setStartDate(filter.start);
    setEndDate(filter.end);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setIsFiltered(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthlyChartData = monthlySales.map(m => ({
    ...m,
    month_name: monthNames[m.month - 1] || m.month
  }));

  return (
    <div className="space-y-6 p-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-slate-500">Sam Rumah Sales & Performance Analytics</p>
          {isFiltered && (
            <Badge variant="secondary" className="mt-2">
              <Calendar className="h-3 w-3 mr-1" />
              Filtered: {startDate} to {endDate}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={showFilters ? "secondary" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filters Panel */}
      {showFilters && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Date Range Filter
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Filters */}
            <div>
              <Label className="text-sm font-medium text-slate-600 mb-2 block">Quick Filters</Label>
              <div className="flex flex-wrap gap-2">
                {quickFilters.map((filter) => (
                  <Button
                    key={filter.label}
                    variant="outline"
                    size="sm"
                    onClick={() => applyQuickFilter(filter)}
                    className={startDate === filter.start && endDate === filter.end ? 'bg-primary text-white' : ''}
                    data-testid={`quick-filter-${filter.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <Label htmlFor="start-date" className="text-sm font-medium text-slate-600">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="end-date" className="text-sm font-medium text-slate-600">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                  data-testid="end-date-input"
                />
              </div>
              <Button onClick={fetchData} disabled={!startDate || !endDate} data-testid="apply-filter-btn">
                Apply Filter
              </Button>
              {isFiltered && (
                <Button variant="ghost" onClick={clearFilters} data-testid="clear-filter-btn">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Period Comparison */}
            <div className="pt-4 border-t">
              <Label className="text-sm font-medium text-slate-600 mb-3 block">Compare Periods (e.g., Q1 2025 vs Q1 2024)</Label>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Period 1 Start</Label>
                  <Input
                    type="date"
                    value={period1Start}
                    onChange={(e) => setPeriod1Start(e.target.value)}
                    className="mt-1"
                    data-testid="period1-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Period 1 End</Label>
                  <Input
                    type="date"
                    value={period1End}
                    onChange={(e) => setPeriod1End(e.target.value)}
                    className="mt-1"
                    data-testid="period1-end-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Period 2 Start</Label>
                  <Input
                    type="date"
                    value={period2Start}
                    onChange={(e) => setPeriod2Start(e.target.value)}
                    className="mt-1"
                    data-testid="period2-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Period 2 End</Label>
                  <Input
                    type="date"
                    value={period2End}
                    onChange={(e) => setPeriod2End(e.target.value)}
                    className="mt-1"
                    data-testid="period2-end-input"
                  />
                </div>
                <Button onClick={fetchComparison} variant="secondary" data-testid="compare-btn">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Compare
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Comparison Results */}
      {showComparison && comparisonData && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Period Comparison
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowComparison(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Period 1 */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm font-medium text-slate-500 mb-2">
                  Period 1: {comparisonData.period1.start} to {comparisonData.period1.end}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Bookings</span>
                    <span className="font-semibold">{formatNumber(comparisonData.period1.stats.total_bookings)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sales</span>
                    <span className="font-semibold">{formatCurrency(comparisonData.period1.stats.total_sales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Commission</span>
                    <span className="font-semibold">{formatCurrency(comparisonData.period1.stats.total_commission)}</span>
                  </div>
                </div>
              </div>

              {/* Period 2 */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm font-medium text-slate-500 mb-2">
                  Period 2: {comparisonData.period2.start} to {comparisonData.period2.end}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Bookings</span>
                    <span className="font-semibold">{formatNumber(comparisonData.period2.stats.total_bookings)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sales</span>
                    <span className="font-semibold">{formatCurrency(comparisonData.period2.stats.total_sales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Commission</span>
                    <span className="font-semibold">{formatCurrency(comparisonData.period2.stats.total_commission)}</span>
                  </div>
                </div>
              </div>

              {/* Changes */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm font-medium text-slate-500 mb-2">% Change (P1 vs P2)</div>
                <div className="space-y-2">
                  {Object.entries(comparisonData.changes).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                      <Badge className={value >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                        {value >= 0 ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
                        {Math.abs(value)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Agents"
          value={formatNumber(summary?.total_agents)}
          subtitle="Active agents in system"
          icon={Users}
        />
        <StatCard 
          title="Total Sales Value"
          value={formatCurrency(summary?.stats?.total_sales)}
          subtitle="All time SPA value"
          icon={Building2}
        />
        <StatCard 
          title="Total Commission"
          value={formatCurrency(summary?.stats?.total_commission)}
          subtitle="All time commission"
          icon={DollarSign}
        />
        <StatCard 
          title="Active Agents"
          value={formatNumber(summary?.stats?.active_agents)}
          subtitle="Agents with sales"
          icon={Target}
        />
      </div>

      {/* Yearly Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summary?.yearly_summary?.map((year) => (
          <Card key={year.year} className="border-l-4 border-l-primary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Year {Math.floor(year.year)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Bookings</span>
                <span className="font-semibold">{formatNumber(year.bookings)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Sales</span>
                <span className="font-semibold">{formatCurrency(year.sales)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Commission</span>
                <span className="font-semibold text-primary">{formatCurrency(year.commission)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="leaderboard" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <Trophy className="h-4 w-4 mr-2" />
            Top Performers
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">
            <TrendingUp className="h-4 w-4 mr-2" />
            Sales Trends
          </TabsTrigger>
          <TabsTrigger value="hierarchy" data-testid="tab-hierarchy">
            <Users className="h-4 w-4 mr-2" />
            Agent Hierarchy
          </TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">
            <Award className="h-4 w-4 mr-2" />
            Commission Workflow
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Top 10 Performers by Commission
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Leaderboard performers={topPerformers} loading={loading} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sales">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Sales by Year</CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={salesByYear} 
                  labelKey="year" 
                  valueKey="total_sales_value"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Monthly Sales ({selectedYear})</CardTitle>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-32" data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2026">2026</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={monthlyChartData} 
                  labelKey="month_name" 
                  valueKey="total_sales_value"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="hierarchy">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Agent Hierarchy by Rank
              </CardTitle>
            </CardHeader>
            <CardContent>
              <HierarchyTree hierarchy={hierarchy} loading={loading} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflow">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                Commission Approval Workflow Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WorkflowStatus data={workflow} loading={loading} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rank Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Rank Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {summary?.rank_distribution?.map((rank, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border"
              >
                <Badge variant="outline" className="font-mono">
                  {rank.rank_code || 'Unknown'}
                </Badge>
                <div>
                  <div className="font-semibold">{rank.count}</div>
                  <div className="text-xs text-slate-500">{rank.rank_name || 'No Rank'}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
